import { BaseText } from '@/src/components/BaseText'
import { DataCompletenessIndicator } from '@/src/components/DataCompletenessIndicator/DataCompletenessIndicator'
import TagComponent from '@/src/components/shared/tag/TagComponent'
import {
  LAST_365_DAYS,
  LAST_365_DAYS_UPDATED,
} from '@/src/features/procurement-and-markets/load-planning/ReferenceTimePeriod/ReferenceTimePeriod.utils'
import { useLoadPlanningDataCompleteness } from '@/src/features/procurement-and-markets/load-planning/hooks/useLoadPlanningDataCompleteness.hook'
import { numberFormat } from '@/src/shared/utils'
import { appColors } from '@/src/theme/foundations/colors'
import { Box } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { BsExclamationTriangle } from 'react-icons/bs'

const LOW_LOCATION_COMPLETENESS_THRESHOLD_PERCENTAGE = 50
const FULL_QUALITY_PERCENTAGE = 100

interface ReferenceTimePeriodOptionLabelProps {
  label: string
  optionValue: string
  tagLabel?: string | null
  tagVariant?: 'lightGreen' | 'lightBlue' | 'lightAmber'
  startDate?: string
  endDate?: string
  locationIds: number[]
  energyType?: string
}

const isLast365OptionValue = (optionValue: string) => optionValue === LAST_365_DAYS || optionValue === LAST_365_DAYS_UPDATED

export const ReferenceTimePeriodOptionLabel = ({
  label,
  optionValue,
  tagLabel,
  tagVariant,
  startDate,
  endDate,
  locationIds,
  energyType,
}: ReferenceTimePeriodOptionLabelProps): JSX.Element => {
  const { t } = useTranslation()
  const { items, scope, qualityPercentage, isReady, isLoading, timeframe } = useLoadPlanningDataCompleteness({
    locationIds,
    energyType,
    startDate,
    endDate,
  })

  const isLast365Option = isLast365OptionValue(optionValue)
  const hasInsufficientLocationCompleteness =
    !isLast365Option &&
    !isLoading &&
    isReady &&
    items.some(item => item.completenessPercentage == null || item.completenessPercentage <= LOW_LOCATION_COMPLETENESS_THRESHOLD_PERCENTAGE)
  const isEffectivelyDisabled = !isLast365Option && (isLoading || hasInsufficientLocationCompleteness)

  const textColor = isEffectivelyDisabled ? appColors.gray[400] : undefined
  const showCompletenessIndicator =
    isReady && (isEffectivelyDisabled || qualityPercentage == null || qualityPercentage < FULL_QUALITY_PERCENTAGE)
  const hasTag = isReady && Boolean(tagLabel && tagVariant) && !showCompletenessIndicator

  const blockSelection = (event: React.SyntheticEvent) => {
    event.stopPropagation()
    event.preventDefault()
  }

  return (
    <Box
      onMouseDown={isEffectivelyDisabled ? blockSelection : undefined}
      onClick={isEffectivelyDisabled ? blockSelection : undefined}
      onTouchEnd={isEffectivelyDisabled ? blockSelection : undefined}
      cursor={isEffectivelyDisabled ? 'default' : undefined}
      padding='8px 12px'
      height='32px'
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      gap='8px'
      width='100%'
      boxSizing='border-box'
    >
      <BaseText asBox color={textColor}>
        {label}
      </BaseText>
      {hasTag && <TagComponent text={tagLabel!} variant={tagVariant!} height='20px' fitContent />}
      {showCompletenessIndicator && (
        <Box onMouseDown={blockSelection} onClick={blockSelection} onTouchEnd={blockSelection}>
          <DataCompletenessIndicator
            withPortal={false}
            icon={<BsExclamationTriangle size={12} />}
            items={items}
            scope={scope}
            timeframe={timeframe}
            qualityPercentage={qualityPercentage}
            onlyShowIncompleteItems
            description={t('components.procurement.forecasting.data_completeness_description')}
          >
            {hasInsufficientLocationCompleteness ? (
              <TagComponent
                text={t('components.procurement.forecasting.reference_period_modal.reference_period_tag_disabled', {
                  qualityPercentage: qualityPercentage == null ? '-' : numberFormat(qualityPercentage),
                })}
                variant='lightAmber'
                height='20px'
                fitContent
                icon={<BsExclamationTriangle size={12} />}
              />
            ) : null}
          </DataCompletenessIndicator>
        </Box>
      )}
    </Box>
  )
}
